/*

  gatadagsins-help.ts

  Help dialog for Gáta Dagsins

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjálmur Þorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { Component, m } from "./mithril";
import { glyph } from "./util";

interface IHelpAttributes {
  onClose: () => void;
}

export const GataDagsinsHelp: Component<IHelpAttributes> = {
  view: (vnode) => {
    const closeHelp = vnode.attrs.onClose;

    return [
      // Backdrop
      m(".modal-backdrop-netskrafl", {
        onclick: (e: Event) => { e.preventDefault(); },
        onwheel: (e: Event) => { e.preventDefault(); e.stopPropagation(); },
        ontouchmove: (e: Event) => { e.preventDefault(); e.stopPropagation(); }
      }),
      m(".modal-dialog.gatadagsins-help",
        m(".modal-content",
          [
            // Close button (positioned absolutely in top-right corner)
            m("button.close",
              {
                onclick: closeHelp,
                "aria-label": "Loka"
              },
              glyph("remove")
            ),

            // Header
            m(".modal-header",
              m("h2", "Um Gátu dagsins")
            ),

            // Body with help content
            m(".modal-body",
              [
                m("p",
                  "Gáta dagsins er dagleg krossgátuþraut, svipuð skrafli, þar sem þú reynir að finna " +
                  "stigahæsta orðið sem hægt er að mynda með gefnum stöfum."
                ),

                m("h3", "Hvernig á að spila"),
                m("ul",
                  [
                    m("li",
                      "Þú færð borð með allmörgum stöfum sem þegar hafa verið lagðir."
                    ),
                    m("li",
                      "Neðst á skjánum eru stafaflísar sem þú getur notað til að mynda orð."
                    ),
                    m("li",
                      "Dragðu flísar á borðið til að mynda orð, annaðhvort lárétt eða lóðrétt."
                    ),
                    m("li",
                      "Orðin verða að tengjast við stafi sem fyrir eru á borðinu."
                    ),
                    m("li",
                      "Þú sérð jafnóðum hvort lögnin á borðinu er gild og hversu mörg stig hún gefur."
                    ),
                    m("li",
                      "Þú getur prófað eins mörg orð og þú vilt - besta skorið þitt er vistað."
                    ),
                  ]
                ),

                m("h3", "Stigagjöf"),
                m("p",
                  "Þú færð stig fyrir hvern staf í orðinu, auk bónusstiga fyrir lengri orð:"
                ),
                m("ul",
                  [
                    m("li", "Hver stafur gefur 1-10 stig eftir gildi hans"),
                    m("li", "Orð sem nota allar 7 stafaflísarnar gefa 50 stiga bónus"),
                    m("li", [
                      "Sumir reitir á borðinu ",
                      m("span.help-bonus-square.double-letter"),
                      "tvöfalda eða ",
                      m("span.help-bonus-square.triple-letter"),
                      "þrefalda stafgildið"
                    ]),
                    m("li", [
                      "Sumir reitir ",
                      m("span.help-bonus-square.double-word"),
                      "tvöfalda eða ",
                      m("span.help-bonus-square.triple-word"),
                      "þrefalda heildarorðgildið"
                    ]),
                  ]
                ),

                m("h3", "Hitamælir"),
                m("p",
                  "Hitamælirinn hægra megin við borðið (sést ekki á farsímum) sýnir:"
                ),
                m("ul",
                  [
                    m("li",
                      m("strong", "Besta mögulega lögn:"),
                      " Hæstu stig sem hægt er að ná á þessu borði."
                    ),
                    m("li",
                      m("strong", "Besta skor dagsins:"),
                      " Hæstu stig sem einhver leikmaður hefur náð í dag."
                    ),
                    m("li",
                      m("strong", "Þín bestu orð:"),
                      " Orðin sem þú hefur lagt og stigin fyrir þau."
                    ),
                    m("li", "Þú getur smellt á orð á hitamælinum til að fá þá lögn aftur á borðið."),
                  ]
                ),

                m("h3", "Ábendingar"),
                m("ul",
                  [
                    m("li",
                      "Reyndu að nota dýra stafi (eins og X, Ý, Þ) á tvöföldunar- eða þreföldunarreitum."
                    ),
                    m("li",
                      "Lengri orð gefa mun fleiri stig vegna bónussins."
                    ),
                    m("li",
                      "Þú getur dregið allar flísar til baka með bláa endurkalls-hnappnum."
                    ),
                    m("li",
                      "Ný gáta birtist á hverjum nýjum degi - klukkan 00:00!"
                    ),
                  ]
                ),

                m("h3", "Um leikinn"),
                m("p",
                  [
                    "Gáta dagsins er systkini ",
                    m("a", { href: "https://netskrafl.is", target: "_blank" }, "Netskrafls"),
                    ", hins sívinsæla íslenska krossgátuleiks á netinu. ",
                    "Leikurinn er þróaður af Miðeind ehf."
                  ]
                ),
              ]
            ),

            // Footer with close button
            m(".modal-footer",
              m("button.btn.btn-primary",
                {
                  onclick: closeHelp
                },
                "Loka"
              )
            )
          ]
        )
      ),
    ];
  }
};
